/**
 * SyncJobProvider - app-wide context that polls for a running sync job.
 * Exposes `runningJob`, `dismissJob`, and `isDismissed` so any component
 * (particularly the global SyncBanner) can react to background job state.
 */
import React from 'react';
import { buildApiUrl } from '../config';

/** Dispatched when active sync job goes away; useProductOnboarding listens (same string there). */
const SYNC_JOB_FINISHED_EVENT = 'klaris:sync-job-finished';

// Re-use the shared type from DataSyncPage rather than duplicating it
export interface SyncStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  substeps?: SyncStep[];
}

export interface RunningSyncJob {
  job_id: string;
  tenant_id: string;
  connector_ids: string[];
  status: string;
  progress: { steps: SyncStep[] } | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at: string | null;
}

interface SyncJobContextValue {
  runningJob: RunningSyncJob | null;
  /**
   * Push a job update directly into the context - call with a running/pending job to
   * show the banner immediately (no wait for next poll), or with `null` to clear it
   * as soon as the job detail page observes completion.
   */
  notifyJobUpdate: (job: RunningSyncJob | null) => void;
  dismissJob: (jobId: string) => void;
  isDismissed: (jobId: string) => boolean;
}

export const SyncJobContext = React.createContext<SyncJobContextValue>({
  runningJob: null,
  notifyJobUpdate: () => {},
  dismissJob: () => {},
  isDismissed: () => false,
});

const POLL_ACTIVE_INTERVAL = 12_000;  // 12 s while a job is running/pending
const POLL_IDLE_INTERVAL   = 60_000;  // 60 s when nothing is active
const DISMISSED_LS_KEY = 'klaris_dismissed_sync_jobs';

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_LS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  // Cap to last 50 so localStorage doesn't grow unboundedly
  localStorage.setItem(DISMISSED_LS_KEY, JSON.stringify(Array.from(ids).slice(-50)));
}

export default function SyncJobProvider({ children }: { children: React.ReactNode }) {
  const [runningJob, setRunningJob] = React.useState<RunningSyncJob | null>(null);
  const [dismissed, setDismissed] = React.useState<Set<string>>(loadDismissed);
  const prevRunningJobIdRef = React.useRef<string | null>(null);

  const notifyJobUpdate = React.useCallback((job: RunningSyncJob | null) => {
    setRunningJob(job);
  }, []);

  // When a tracked job disappears from the active slot, onboarding may have advanced on the server
  // (e.g. data_model_built) even if the user left the job detail page - refetch checklist consumers.
  React.useEffect(() => {
    const id = runningJob?.job_id ?? null;
    const prev = prevRunningJobIdRef.current;
    if (prev !== null && id === null) {
      window.dispatchEvent(new CustomEvent(SYNC_JOB_FINISHED_EVENT));
    }
    prevRunningJobIdRef.current = id;
  }, [runningJob]);

  const dismissJob = React.useCallback((jobId: string) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(jobId);
      saveDismissed(next);
      return next;
    });
  }, []);

  const isDismissed = React.useCallback(
    (jobId: string) => dismissed.has(jobId),
    [dismissed],
  );

  // Stable context object: only changes when one of the four values actually changes.
  // Without this, every job-poll re-render (12 s / 60 s) creates a new value reference
  // and forces all SyncJobContext consumers to re-render unnecessarily.
  const contextValue = React.useMemo(
    () => ({ runningJob, notifyJobUpdate, dismissJob, isDismissed }),
    [runningJob, notifyJobUpdate, dismissJob, isDismissed],
  );

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = (hasActive: boolean) => {
      if (cancelled) return;
      const delay = hasActive ? POLL_ACTIVE_INTERVAL : POLL_IDLE_INTERVAL;
      timeoutId = setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const raw = localStorage.getItem('klaris_tenant');
        if (!raw) { scheduleNext(false); return; }
        const t = JSON.parse(raw);
        const tenantId: string = t?.tenant_id || t?.id || '';
        if (!tenantId) { scheduleNext(false); return; }

        const token = localStorage.getItem('klaris_jwt');
        if (!token) { scheduleNext(false); return; }

        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs?limit=5`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) { scheduleNext(false); return; }

        const jobs: RunningSyncJob[] = await resp.json();
        const active = (Array.isArray(jobs) ? jobs : []).find(
          (j) => j.status === 'running' || j.status === 'pending',
        );
        setRunningJob(active || null);
        scheduleNext(!!active);
      } catch {
        // silent - network errors shouldn't disrupt the UI
        scheduleNext(false);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <SyncJobContext.Provider value={contextValue}>
      {children}
    </SyncJobContext.Provider>
  );
}
